"use client";

import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { BRUNCH_ADDONS } from "@/lib/order-config";
import { formatPrice } from "@/stores/cart-store";
import { staggerContainer, listItem, pressScale } from "@/lib/animations";

interface BrunchCustomizerProps {
  selectedAddons: string[];
  onAddonsChange: (addons: string[]) => void;
}

export function BrunchCustomizer({
  selectedAddons,
  onAddonsChange,
}: BrunchCustomizerProps) {
  const toggleAddon = (value: string) => {
    if (selectedAddons.includes(value)) {
      onAddonsChange(selectedAddons.filter((a) => a !== value));
    } else {
      onAddonsChange([...selectedAddons, value]);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-base text-foreground">
          Add-ons
        </h4>
        <span className="text-xs text-muted-foreground font-normal">
          Optional
        </span>
      </div>

      {/* Add-on Buttons */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="flex gap-2"
      >
        {BRUNCH_ADDONS.map((addon) => {
          const isSelected = selectedAddons.includes(addon.value);

          return (
            <motion.button
              key={addon.value}
              variants={listItem}
              {...pressScale}
	              type="button"
	              aria-pressed={isSelected}
	              onClick={() => toggleAddon(addon.value)}
	              className={cn(
	                "flex-1 h-16 rounded-xl text-base font-medium transition-all touch-manipulation active:scale-95 sm:h-14 sm:text-sm",
	                "border flex flex-col items-center justify-center gap-0.5 relative",
	                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isSelected
                  ? "bg-foreground text-background border-foreground shadow-md"
                  : "bg-card text-card-foreground border-border hover:bg-muted"
              )}
            >
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-1.5 right-1.5">
                  <Check className="h-3 w-3 text-background" />
                </div>
              )}
              {!isSelected && (
                <div className="absolute top-1.5 right-1.5">
                  <Plus className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
              <span className="font-semibold">{addon.label}</span>
              <span
                className={cn(
                  "text-xs",
                  isSelected
                    ? "text-background/80"
                    : "text-muted-foreground"
                )}
              >
                +{formatPrice(addon.price)}
              </span>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}
